// 치과 「접수」(데스크 QR 대기 순서) 공용 상수·검증·문구 (2026-10-03 대표 지시 — 정본: 승인된 시뮬레이터 qr_waiting_sim.html)
// 원칙: 입력값은 localStorage/sessionStorage 에 저장하지 않는다(대기표 ticket 만 sessionStorage). 콘솔에 이름·번호·주소 0 — 오류는 코드까지만.
// 의료법: 혜택·할인·무료·후기 표현 없음 — 사실 안내만.
import { isValidWalkinName, isValidWalkinPhone, formatWalkinPhoneInput, WALKIN_NAME_HINT } from './clinicWalkin';

export { isValidWalkinName as isValidReceptionName, isValidWalkinPhone as isValidReceptionTel, formatWalkinPhoneInput as formatReceptionTelInput, WALKIN_NAME_HINT as RECEPTION_NAME_HINT };

// 오신 이유 — 시뮬레이터 순서(검진·치료·상담·기타) 고정 4종, 자유입력 없음. 서버로는 key 를 보낸다
export const RECEPTION_REASONS = [
    { key: 'checkup', label: '검진' },
    { key: 'treatment', label: '치료' },
    { key: 'consult', label: '상담' },
    { key: 'other', label: '기타' },
];
export const reasonLabel = (k) => (RECEPTION_REASONS.find(r => r.key === k) || {}).label || String(k || '-');

// 성별 — 화면 '여'/'남', 서버 'F'/'M'
export const RECEPTION_SEXES = [{ key: 'F', label: '여' }, { key: 'M', label: '남' }];
export const sexLabel = (k) => (RECEPTION_SEXES.find(s => s.key === k) || {}).label || String(k || '-');

export const kindLabel = (k) => (k === 'new' ? '처음 온 환자' : '기존 환자');

// ── 데스크 직접 추가 (2026-10-04 기획서_접수_수동추가 — 대표 결정 5건 권고대로)
//   서버 listTodayReception item.desk === true(또는 source 'desk') = 데스크가 직접 넣은 대기 환자. kind:null 이라 kindLabel('기존 환자')로 보이면 안 된다
//   그날 대기 순서에만 쓰고 자정 마감 뒤 삭제 — 접수 기록·이름 검색·의뢰서 연결 대상 아님, 상세(접수 카드) 없음
export const isDeskEntry = (it) => !!it && (it.desk === true || it.source === 'desk');
export const DESK_ADD_BADGE = '데스크 추가';
/** 접수 구분 라벨 — 데스크 추가 건이면 '데스크 추가', 아니면 처음 온/기존 */
export const receptionKindLabelOf = (it) => (isDeskEntry(it) ? DESK_ADD_BADGE : kindLabel(it?.kind));
// ── 케어 앱 접수 (2026-10-11 계획_케어앱_예약대기 W3) — 서버 todayItem source 'app'(계획 S7). 호출·삭제·연결 동작은 QR 접수와 같고 배지만 다르다
export const isAppEntry = (it) => !!it && !isDeskEntry(it) && (it.source === 'app' || it.via === 'app');
export const APP_RECEPTION_BADGE = '앱 접수';
/** 서버 deskAddFrom('YYYY-MM-DD') → '10월 11일' */
export function fmtMonthDay(k) {
    const m = String(k || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${Number(m[2])}월 ${Number(m[3])}일` : String(k || '');
}

// 상세 정보 칸 자동 닫힘(초) — 대기실에서 데스크 화면이 보일 수 있어서 (시뮬레이터 AUTO_CLOSE)
export const RECEPTION_AUTO_CLOSE_SEC = 30;
// 데스크 목록·환자 대기표 갱신 주기 — onSnapshot 불가(컬렉션 클라이언트 차단) → 폴링
export const RECEPTION_POLL_MS = 5000;

// ── 동의 문안 정본 (법적보안 docs/정본_데스크접수_동의·방침_2026-10-04.md §1-2·§1-3 — 서버 functions/lib/desk-reception.js CONSENT_SOURCES 와 바이트 동일)
//   해시 대상 원문 = label + '\n' + detail ({clinicName} 치환 전), NFC·CRLF→LF·trim·sha256. label 에는 {clinicName} 이 없다(치환 없이 그대로 표시).
//   문안을 한 글자라도 바꾸면 version 을 올리고 서버·웹 해시를 같이 다시 계산한다(웹이 먼저 나가면 consent-mismatch).
//   ageMode: 'self14'(본인, 만 14세 이상) | 'guardian'(법정대리인 — 부·모·후견인이 만 14세 미만 아이 대신 작성)
export const RECEPTION_CONSENT = {
    old: {
        version: 'desk-old-1.0',
        ageMode: 'self14',
        label: '[필수] 접수를 위해 이름과 오신 이유를 수집하고, 접수일부터 2년 보관하는 데 동의합니다.',
        detail: [
            '{clinicName}은(는) 아래와 같이 개인정보를 받습니다.',
            '- 받는 항목: 이름, 오신 이유(검진·치료·상담·기타 중 선택)',
            '- 쓰는 곳: 오늘 대기 순서 안내, 다음에 오셨을 때 지난 접수 기록 확인, 같은 날 같은 이름으로 작성된 보철물 의뢰 기록과 연결',
            '- 보관 기간: 접수일부터 2년. 기간이 지나면 자동으로 삭제합니다.',
            '- 동의하지 않으셔도 됩니다. 동의하지 않으시면 데스크에서 직접 접수하시면 되고, 진료는 똑같이 받으실 수 있습니다.',
            '- 처리 위탁: {clinicName}은(는) 이 정보의 처리를 주식회사 덴탈클러스터에 맡기고, 덴탈클러스터는 저장·서버 기능을 Google LLC에 다시 맡깁니다(국외(미국 등)에서 처리될 수 있습니다). 덴탈클러스터는 맡은 일 외에는 이 정보를 쓰지 않습니다.',
            '- 이 기록은 진료기록이 아닙니다. 진료 기록은 {clinicName}이(가) 따로 작성·보관합니다. 보기·고치기·지우기 요청은 {clinicName} 데스크에 해 주세요.',
        ].join('\n'),
        textHash: 'cb3ebb477ee85091d7b0c02e7cac789fdda0b973b3d3c9afd83e2c62378953b0',
    },
    new: {
        version: 'desk-new-1.0',
        ageMode: 'self14',
        label: '[필수] 접수를 위해 이름·생년월일·성별·휴대폰 번호·주소·오신 이유를 수집하고, 접수일부터 2년 보관하는 데 동의합니다.',
        detail: [
            '{clinicName}은(는) 종이 접수 카드 대신 아래와 같이 개인정보를 받습니다.',
            '- 받는 항목: 이름, 생년월일, 성별, 휴대폰 번호, 주소, 오신 이유(검진·치료·상담·기타 중 선택)',
            '- 쓰는 곳: 오늘 접수와 대기 순서 안내, 다음에 오셨을 때 지난 접수 기록 확인, 같은 날 같은 이름으로 작성된 보철물 의뢰 기록과 연결',
            '- 보관 기간: 접수일부터 2년. 기간이 지나면 자동으로 삭제합니다.',
            '- 이 동의로 광고·홍보 연락을 하지 않습니다.',
            '- 동의하지 않으셔도 됩니다. 동의하지 않으시면 데스크에서 직접 접수하시면 되고, 진료는 똑같이 받으실 수 있습니다.',
            '- 처리 위탁: {clinicName}은(는) 이 정보의 처리를 주식회사 덴탈클러스터에 맡기고, 덴탈클러스터는 저장·서버 기능을 Google LLC에 다시 맡깁니다(국외(미국 등)에서 처리될 수 있습니다). 덴탈클러스터는 맡은 일 외에는 이 정보를 쓰지 않습니다.',
            '- 이 기록은 진료기록이 아닙니다. 진료 기록은 {clinicName}이(가) 따로 작성·보관합니다. 보기·고치기·지우기 요청은 {clinicName} 데스크에 해 주세요.',
        ].join('\n'),
        textHash: 'f54590a5e4bd0b3abd7b955dd2824d42a834e4c9bd6acfbdfff6ab3cff76e999',
    },
};

// ── 보호자(법정대리인) 판 — 정본 §7-2 원문 확정(대표 10-04 결정: 보호자 확인 = §7-5 A안 데스크 대면 확인 →
//   "- 데스크에서 보호자께서 함께 오셨는지 확인할 수 있습니다." 줄 포함). 서버 CONSENT_SOURCES 와 바이트 동일해야 한다.
//   textHash 는 정본 블록·이 상수 양쪽에서 재계산해 대조함(2026-10-04). 문안을 바꾸면 판을 올리고 서버·웹 같이 재계산.
export const RECEPTION_CONSENT_GUARDIAN = {
    old: {
        version: 'desk-old-g-1.0',
        ageMode: 'guardian',
        label: '[필수] 저는 아이(만 14세 미만 환자)의 부모 또는 후견인으로서, 접수를 위해 아이의 이름·오신 이유와 제 이름·아이와의 관계를 수집하고 접수일부터 2년 보관하는 데 동의합니다.',
        detail: [
            '{clinicName}은(는) 만 14세 미만 아이의 접수를 위해, 법정대리인(부모 또는 후견인)의 동의를 받아 아래와 같이 개인정보를 받습니다.',
            '- 받는 항목: 아이의 이름, 오신 이유(검진·치료·상담·기타 중 선택). 그리고 동의하시는 분의 이름과 아이와의 관계(부·모·후견인)',
            '- 쓰는 곳: 오늘 대기 순서 안내, 다음에 오셨을 때 지난 접수 기록 확인, 같은 날 같은 이름으로 작성된 보철물 의뢰 기록과 연결. 동의하시는 분의 이름과 관계는 법정대리인이 동의했다는 기록으로만 씁니다.',
            '- 보관 기간: 접수일부터 2년. 기간이 지나면 동의하신 분의 정보와 함께 자동으로 삭제합니다.',
            '- 동의하지 않으셔도 됩니다. 동의하지 않으시면 데스크에서 직접 접수하시면 되고, 진료는 똑같이 받으실 수 있습니다.',
            '- 부모나 후견인이 아닌 분(할머니·할아버지·친척·선생님 등)은 이 동의를 하실 수 없습니다. 데스크에서 접수해 주세요.',
            '- 데스크에서 보호자께서 함께 오셨는지 확인할 수 있습니다.',
            '- 처리 위탁: {clinicName}은(는) 이 정보의 처리를 주식회사 덴탈클러스터에 맡기고, 덴탈클러스터는 저장·서버 기능을 Google LLC에 다시 맡깁니다(국외(미국 등)에서 처리될 수 있습니다). 덴탈클러스터는 맡은 일 외에는 이 정보를 쓰지 않습니다.',
            '- 이 기록은 진료기록이 아닙니다. 진료 기록은 {clinicName}이(가) 따로 작성·보관합니다. 아이 정보의 보기·고치기·지우기 요청은 부모 또는 후견인께서 {clinicName} 데스크에 해 주세요.',
        ].join('\n'),
        textHash: '1283ab83cdab2d22e0cf80137c82a96befb342da4a5fd70b529109858535ba39',
    },
    new: {
        version: 'desk-new-g-1.0',
        ageMode: 'guardian',
        label: '[필수] 저는 아이(만 14세 미만 환자)의 부모 또는 후견인으로서, 접수를 위해 아이의 이름·생년월일·성별·주소·오신 이유, 연락받을 휴대폰 번호, 제 이름·아이와의 관계를 수집하고 접수일부터 2년 보관하는 데 동의합니다.',
        detail: [
            '{clinicName}은(는) 만 14세 미만 아이의 접수를 위해, 종이 접수 카드 대신 법정대리인(부모 또는 후견인)의 동의를 받아 아래와 같이 개인정보를 받습니다.',
            '- 받는 항목: 아이의 이름, 생년월일, 성별, 주소, 오신 이유(검진·치료·상담·기타 중 선택), 연락받을 휴대폰 번호(아이 또는 보호자 번호). 그리고 동의하시는 분의 이름과 아이와의 관계(부·모·후견인)',
            '- 쓰는 곳: 오늘 접수와 대기 순서 안내, 다음에 오셨을 때 지난 접수 기록 확인, 같은 날 같은 이름으로 작성된 보철물 의뢰 기록과 연결. 동의하시는 분의 이름과 관계는 법정대리인이 동의했다는 기록으로만 씁니다.',
            '- 보관 기간: 접수일부터 2년. 기간이 지나면 동의하신 분의 정보와 함께 자동으로 삭제합니다.',
            '- 이 동의로 광고·홍보 연락을 하지 않습니다.',
            '- 동의하지 않으셔도 됩니다. 동의하지 않으시면 데스크에서 직접 접수하시면 되고, 진료는 똑같이 받으실 수 있습니다.',
            '- 부모나 후견인이 아닌 분(할머니·할아버지·친척·선생님 등)은 이 동의를 하실 수 없습니다. 데스크에서 접수해 주세요.',
            '- 데스크에서 보호자께서 함께 오셨는지 확인할 수 있습니다.',
            '- 처리 위탁: {clinicName}은(는) 이 정보의 처리를 주식회사 덴탈클러스터에 맡기고, 덴탈클러스터는 저장·서버 기능을 Google LLC에 다시 맡깁니다(국외(미국 등)에서 처리될 수 있습니다). 덴탈클러스터는 맡은 일 외에는 이 정보를 쓰지 않습니다.',
            '- 이 기록은 진료기록이 아닙니다. 진료 기록은 {clinicName}이(가) 따로 작성·보관합니다. 아이 정보의 보기·고치기·지우기 요청은 부모 또는 후견인께서 {clinicName} 데스크에 해 주세요.',
        ].join('\n'),
        textHash: '52d249f70ef5cb801a449ba5886bbd594b04ad90c5e98a66118697469030571a',
    },
};
/** 보호자 판 정본·해시가 확정됐는가 — 아니면 보호자 접수는 "준비 중" 안내(서버 guardian-not-ready 와 같은 결과) */
export const isGuardianConsentReady = (kind) => {
    const c = RECEPTION_CONSENT_GUARDIAN[kind === 'new' ? 'new' : 'old'];
    return !!(c && c.label && c.detail && typeof c.textHash === 'string' && /^[0-9a-f]{64}$/.test(c.textHash));
};
/** kind·작성자 모드에 맞는 동의 판 */
export const receptionConsentFor = (kind, ageMode) =>
    (ageMode === 'guardian' ? RECEPTION_CONSENT_GUARDIAN : RECEPTION_CONSENT)[kind === 'new' ? 'new' : 'old'];
// 해시 대상 원문 = label + '\n' + detail ({clinicName} 치환 전)
export const consentSourceText = (c) => `${c.label}\n${c.detail}`;
export const consentDetailText = (c, clinicName) =>
    String(c?.detail || '').split('{clinicName}').join(String(clinicName || '').trim() || '이 치과');

// 보호자와의 관계 — 법정대리인만(정본 §7-1: 기타 보호자 없음). 서버 enum 과 같은 값(father|mother|guardian)
export const GUARDIAN_RELATIONS = [
    { key: 'father', label: '부' },
    { key: 'mother', label: '모' },
    { key: 'guardian', label: '후견인' },
];
export const guardianRelationLabel = (k) => (GUARDIAN_RELATIONS.find(r => r.key === k) || {}).label || String(k || '-');

// ── 치과용 「데스크 접수 처리위탁 확인」 — 판별 원문(코드 블록 10줄, 바이트 그대로 표시·해시). 서버 CLINIC_CONSENT_SOURCES 와 같은 원문.
//   1.1 = 정본 §7-6 (QR 접수 — 1.0(§3-4) 대비 1항 법정대리인 정보·5항⑥ 동행 확인). 1.1 확인 치과는 QR 접수를 계속 받는다(서버 표에 1.1 유지).
//   1.2 = 정본 §8-2 (1.1 + 데스크 직접 입력 — 2줄·1항·4항·5항⑦). 직접 추가는 1.2 확인 치과만(서버 desk-add-consent-required).
//   마지막 줄 '☐ …' 는 원문 그대로 둔다(해시 대상). 화면의 실제 체크박스는 원문 블록 아래에 따로 그린다.
//   확인 카드는 서버가 알려 준 현재 판(current·currentTextHash)과 같은 판을 이 표에서 찾아 보인다 — 없으면 "새로고침" 안내만 하고 보내지 않는다.
export const DESK_CLINIC_CONSENT_V11 = {
    version: 'desk-clinic-1.1',
    lines: [
        '[필수] 데스크 접수 처리위탁 확인',
        '본원은 데스크 QR 접수 업무의 처리를 주식회사 덴탈클러스터(이하 "덴탈클러스터")에 맡기며, 아래 내용을 확인합니다.',
        '1. 맡기는 일: 본원 명의 접수 화면 제공, 환자가 적은 접수 정보(이름·오신 이유, 처음 온 환자는 생년월일·성별·휴대폰 번호·주소 추가, 만 14세 미만 환자는 법정대리인이 적으며 법정대리인의 이름·환자와의 관계 추가)의 저장, 대기 순서 표시, 지난 접수 기록 조회, 같은 날 같은 이름의 의뢰서와 연결, 접수일부터 2년이 지나면 삭제.',
        '2. 덴탈클러스터는 이 정보를 위 목적 밖으로 쓰거나 다른 사람에게 주지 않으며, 다른 치과의 기록과 합치지 않습니다.',
        '3. 재위탁: 본원은 덴탈클러스터가 저장·서버 기능을 Google LLC(Firebase)에 다시 맡기는 것에 동의합니다. 국외(미국 등)에서 처리될 수 있습니다. 그 밖의 재위탁은 본원의 동의를 받아야 합니다.',
        '4. 안전 조치: 본원 대표·직원 계정만 볼 수 있고, 전송 구간은 암호화되며, 접수 카드 상세 열람은 접속기록으로 남습니다.',
        '5. 본원의 일: ①환자의 동의는 본원 명의 화면에서 받고, 동의하지 않은 환자는 데스크에서 접수합니다. ②접수 화면이 켜진 기기는 대기실·환자 쪽에서 보이지 않게 둡니다. ③이 기록은 진료기록부가 아니므로 진료 기록은 따로 작성합니다. ④본원 개인정보 처리방침에 이 위탁을 공개합니다. ⑤환자의 열람·정정·삭제 요구를 처리합니다. ⑥법정대리인이 적은 만 14세 미만 환자의 접수는 데스크에서 법정대리인이 함께 왔는지 확인하고, 확인되지 않으면 데스크에서 다시 접수합니다.',
        '6. 관리·감독: 본원은 처리 현황을 요청할 수 있고 덴탈클러스터는 응합니다. 덴탈클러스터는 유출 등 사고를 알게 되면 지체 없이 본원에 알립니다.',
        '7. 이 확인은 이용약관 제4조의7에 덧붙는 약정입니다. 손해배상 등 책임은 이용약관과 관계 법령에 따릅니다.',
        '☐ 위 내용을 확인했고, 데스크 접수 정보의 처리를 덴탈클러스터에 맡깁니다.',
    ],
    textHash: '43e4f748bbcf86cbeffc8bce41152cb03915ae90edc3429903ff7d73b9a68ccc',
};
// 1.2 — 정본 §8-2 코드 블록을 그대로 옮김. 해시는 정본 블록에서 재계산해 서버 expect 와 일치 확인(2026-10-04).
export const DESK_CLINIC_CONSENT_V12 = {
    version: 'desk-clinic-1.2',
    deskAdd: true,
    lines: [
        '[필수] 데스크 접수 처리위탁 확인',
        '본원은 데스크 접수 업무(QR 접수와 데스크 직접 입력)의 처리를 주식회사 덴탈클러스터(이하 "덴탈클러스터")에 맡기며, 아래 내용을 확인합니다.',
        '1. 맡기는 일: 본원 명의 접수 화면 제공, 환자가 적은 접수 정보(이름·오신 이유, 처음 온 환자는 생년월일·성별·휴대폰 번호·주소 추가, 만 14세 미만 환자는 법정대리인이 적으며 법정대리인의 이름·환자와의 관계 추가)의 저장, 대기 순서 표시, 지난 접수 기록 조회, 같은 날 같은 이름의 의뢰서와 연결, 접수일부터 2년이 지나면 삭제. 그리고 본원 대표·직원이 데스크에서 직접 입력한 대기 환자의 이름·오신 이유의 저장과 대기 순서 표시. 이 정보는 그날 대기 순서 안내와 본원이 의뢰서를 작성할 때 이름을 불러와 채우는 데에만 쓰고, 지난 접수 기록 조회·이름 검색·의뢰서와의 연결에는 쓰지 않으며, 입력한 날 자정에 마감하고 마감 뒤 자동으로 삭제.',
        '2. 덴탈클러스터는 이 정보를 위 목적 밖으로 쓰거나 다른 사람에게 주지 않으며, 다른 치과의 기록과 합치지 않습니다.',
        '3. 재위탁: 본원은 덴탈클러스터가 저장·서버 기능을 Google LLC(Firebase)에 다시 맡기는 것에 동의합니다. 국외(미국 등)에서 처리될 수 있습니다. 그 밖의 재위탁은 본원의 동의를 받아야 합니다.',
        '4. 안전 조치: 본원 대표·직원 계정만 볼 수 있고, 전송 구간은 암호화되며, 접수 카드 상세 열람과 데스크 직접 입력·삭제는 어느 계정이 했는지 접속기록으로 남습니다.',
        '5. 본원의 일: ①환자의 동의는 본원 명의 화면에서 받고, 동의하지 않은 환자는 데스크에서 접수합니다. ②접수 화면이 켜진 기기는 대기실·환자 쪽에서 보이지 않게 둡니다. ③이 기록은 진료기록부가 아니므로 진료 기록은 따로 작성합니다. ④본원 개인정보 처리방침에 이 위탁을 공개합니다. ⑤환자의 열람·정정·삭제 요구를 처리합니다. ⑥법정대리인이 적은 만 14세 미만 환자의 접수는 데스크에서 법정대리인이 함께 왔는지 확인하고, 확인되지 않으면 데스크에서 다시 접수합니다. ⑦데스크 직접 입력에는 대기 순서에 필요한 이름과 오신 이유만 적고, 증상·연락처·주민등록번호 등은 적지 않습니다.',
        '6. 관리·감독: 본원은 처리 현황을 요청할 수 있고 덴탈클러스터는 응합니다. 덴탈클러스터는 유출 등 사고를 알게 되면 지체 없이 본원에 알립니다.',
        '7. 이 확인은 이용약관 제4조의7에 덧붙는 약정입니다. 손해배상 등 책임은 이용약관과 관계 법령에 따릅니다.',
        '☐ 위 내용을 확인했고, 데스크 접수 정보의 처리를 덴탈클러스터에 맡깁니다.',
    ],
    textHash: 'e6b7e10f281eebc0f18e634e860af2b3f2099c51b7924170702d61e4a8110836',
};
// 웹이 아는 판 전부(새 판이 뒤). 현재 판 기본값 = 마지막(1.2) — 서버 current 와 맞는 판을 고를 때는 deskClinicConsentFor 를 쓴다
export const DESK_CLINIC_CONSENTS = [DESK_CLINIC_CONSENT_V11, DESK_CLINIC_CONSENT_V12];
export const DESK_CLINIC_CONSENT = DESK_CLINIC_CONSENT_V12;
/** 서버가 알려 준 판·해시와 바이트 동일한 웹 원문 — 없으면 null(웹·서버 배포 어긋남) */
export const deskClinicConsentFor = (version, textHash) =>
    DESK_CLINIC_CONSENTS.find(c => c.version === version && c.textHash === textHash) || null;
export const deskClinicConsentText = (c = DESK_CLINIC_CONSENT) => c.lines.join('\n');

// ── desk-clinic-1.3 (2026-10-11 계획_케어앱_예약대기 W5·S7) — docs/정본_케어앱_예약대기_문서묶음_2026-10-11.md §3-4 코드 블록 글자 그대로(10줄, 스크립트 추출).
//   ⚠️ 게시 전: textHash 'TBD'(dev-server 계산 예정) → deskClinicConsentFor 가 절대 고르지 않고, DESK_CLINIC_CONSENTS·DESK_CLINIC_CONSENT 에도 넣지 않았다(현재 판 1.2 유지).
//   게시 = 해시 기입(서버 CLINIC_CONSENT_SOURCES 와 같은 값, deskAdd:true 유지) → DESK_CLINIC_CONSENTS 뒤에 추가 → 같은 배포.
export const DESK_CLINIC_CONSENT_V13_DRAFT = {
    status: 'draft',
    version: 'desk-clinic-1.3',
    deskAdd: true,
    lines: [
        "[필수] 데스크 접수 처리위탁 확인",
        "본원은 데스크 접수 업무(QR 접수, 데스크 직접 입력, 덴클 케어 앱 접수)의 처리를 주식회사 덴탈클러스터(이하 \"덴탈클러스터\")에 맡기며, 아래 내용을 확인합니다.",
        "1. 맡기는 일: 본원 명의 접수 화면 제공, 환자가 적은 접수 정보(이름·오신 이유, 처음 온 환자는 생년월일·성별·휴대폰 번호·주소 추가, 만 14세 미만 환자는 법정대리인이 적으며 법정대리인의 이름·환자와의 관계 추가)의 저장, 대기 순서 표시, 지난 접수 기록 조회, 같은 날 같은 이름의 의뢰서와 연결, 접수일부터 2년이 지나면 삭제. 그리고 본원 대표·직원이 데스크에서 직접 입력한 대기 환자의 이름·오신 이유의 저장과 대기 순서 표시. 이 정보는 그날 대기 순서 안내와 본원이 의뢰서를 작성할 때 이름을 불러와 채우는 데에만 쓰고, 지난 접수 기록 조회·이름 검색·의뢰서와의 연결에는 쓰지 않으며, 입력한 날 자정에 마감하고 마감 뒤 자동으로 삭제. 그리고 본원이 앱 접수를 켠 경우, 본원에 환자 앱 등록을 한 만 14세 이상 환자가 덴클 케어 앱으로 본원 데스크 QR을 찍어 본원 명의의 접수 화면에서 한 그날 접수(오신 이유는 환자가 고르고, 이름은 그 환자가 본원에 앱 등록할 때 적은 이름을 옮겨 채움)의 저장, 앱 화면이 열려 있는 동안의 대기 순서 표시, 지난 접수 기록 조회, 같은 날 같은 이름의 의뢰서와 연결, 접수일부터 2년이 지나면 삭제. 앱 접수 기록에는 환자의 앱 계정을 저장하지 않으며, 다녀간 적 있는 환자의 접수만 받습니다.",
        "2. 덴탈클러스터는 이 정보를 위 목적 밖으로 쓰거나 다른 사람에게 주지 않으며, 다른 치과의 기록과 합치지 않습니다.",
        "3. 재위탁: 본원은 덴탈클러스터가 저장·서버 기능을 Google LLC(Firebase)에 다시 맡기는 것에 동의합니다. 국외(미국 등)에서 처리될 수 있습니다. 그 밖의 재위탁은 본원의 동의를 받아야 합니다.",
        "4. 안전 조치: 본원 대표·직원 계정만 볼 수 있고, 전송 구간은 암호화되며, 접수 카드 상세 열람과 데스크 직접 입력·삭제는 어느 계정이 했는지 접속기록으로 남습니다.",
        "5. 본원의 일: ①환자의 동의는 본원 명의 화면에서 받고, 동의하지 않은 환자는 데스크에서 접수합니다. ②접수 화면이 켜진 기기는 대기실·환자 쪽에서 보이지 않게 둡니다. ③이 기록은 진료기록부가 아니므로 진료 기록은 따로 작성합니다. ④본원 개인정보 처리방침에 이 위탁을 공개합니다. ⑤환자의 열람·정정·삭제 요구를 처리합니다. ⑥법정대리인이 적은 만 14세 미만 환자의 접수는 데스크에서 법정대리인이 함께 왔는지 확인하고, 확인되지 않으면 데스크에서 다시 접수합니다. ⑦데스크 직접 입력에는 대기 순서에 필요한 이름과 오신 이유만 적고, 증상·연락처·주민등록번호 등은 적지 않습니다. ⑧앱 접수는 본원이 앱 접수 내용이 들어간 환자 앱 등록 처리위탁 확인을 한 경우에만 켤 수 있으며, 처음 온 환자는 QR 접수나 데스크에서 접수합니다.",
        "6. 관리·감독: 본원은 처리 현황을 요청할 수 있고 덴탈클러스터는 응합니다. 덴탈클러스터는 유출 등 사고를 알게 되면 지체 없이 본원에 알립니다.",
        "7. 이 확인은 이용약관 제4조의7에 덧붙는 약정입니다. 손해배상 등 책임은 이용약관과 관계 법령에 따릅니다.",
        "☐ 위 내용을 확인했고, 데스크 접수 정보의 처리를 덴탈클러스터에 맡깁니다.",
    ],
    textHash: '64823bc3a3b304cb1eb9a66a2e74453e8eb8a63fca55991b5fd4493ff68ecd46', // desk-clinic-1.3 서버 계약 교차 일치(10-11). 게시 전까지 어느 목록에도 넣지 않음(위 주석)
};

// ── 만 나이(접수일 KST 기준) — 서버 ageOn·isUnder14 와 같은 판정(2/29 생은 평년 3/1 에 한 살 — 문자열 비교)
export function ageOnDay(birth, dayKey) {
    const by = Number(String(birth).slice(0, 4)); const dy = Number(String(dayKey).slice(0, 4));
    return dy - by - (String(dayKey).slice(5) < String(birth).slice(5) ? 1 : 0);
}
export const RECEPTION_AGE_OF_CONSENT = 14;
/** 처음 온 환자 생년월일이 오늘(KST) 만 14세 미만인가. 형식 불량 = null(판정 안 함) */
export function isUnder14Kst(birth, now = new Date()) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(birth || ''))) return null;
    return ageOnDay(birth, kstDayKey(now)) < RECEPTION_AGE_OF_CONSENT;
}

// ── 입력 검증 (서버도 같은 규칙으로 다시 검사해야 한다 — dev-server 계약)
// 생년월일 — 숫자만 받아 YYYY-MM-DD 로. 1900년~오늘, 실재 날짜만
export function formatBirthInput(raw) {
    const d = String(raw || '').replace(/\D/g, '').slice(0, 8);
    if (d.length <= 4) return d;
    if (d.length <= 6) return `${d.slice(0, 4)}-${d.slice(4)}`;
    return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
}
export function isValidBirth(v) {
    const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return false;
    const y = +m[1], mo = +m[2], da = +m[3];
    const dt = new Date(y, mo - 1, da);
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== da) return false;
    return y >= 1900 && dt.getTime() <= Date.now();
}
export const RECEPTION_ADDR_MAX = 100;
export function isValidAddr(v) {
    const s = String(v || '').trim();
    return s.length >= 2 && s.length <= RECEPTION_ADDR_MAX;
}

// ── 날짜·시각 (한국 시간 기준)
const pad = (n) => String(n).padStart(2, '0');
export const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
// Date → KST 기준 {y,m(1-12),d,hh,mm,ss,wd}
export function kstParts(date) {
    const t = (date instanceof Date ? date : new Date(date)).getTime() + 9 * 3600 * 1000;
    const k = new Date(t);
    return { y: k.getUTCFullYear(), m: k.getUTCMonth() + 1, d: k.getUTCDate(), hh: k.getUTCHours(), mm: k.getUTCMinutes(), ss: k.getUTCSeconds(), wd: k.getUTCDay() };
}
export function kstDayKey(date = new Date()) {
    const p = kstParts(date);
    return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}
// 'YYYY-MM-DD' → '10.2 (목)'
export function fmtDayKey(k) {
    const [y, m, d] = String(k || '').split('-').map(Number);
    if (!y || !m || !d) return String(k || '');
    return `${m}.${d} (${WEEKDAYS[new Date(y, m - 1, d).getDay()]})`;
}
// 서버 at/calledAt — ms(number) 를 기본으로, 'HH:MM' 문자열·ISO·Firestore Timestamp 모양도 받아 HH:MM(KST)로
export function fmtHM(v) {
    if (v == null || v === '') return '';
    if (typeof v === 'string' && /^\d{1,2}:\d{2}$/.test(v)) return v;
    let ms = null;
    if (typeof v === 'number') ms = v;
    else if (typeof v === 'string') ms = Date.parse(v);
    else if (typeof v?.toMillis === 'function') ms = v.toMillis();
    else if (typeof v?._seconds === 'number') ms = v._seconds * 1000;
    else if (typeof v?.seconds === 'number') ms = v.seconds * 1000;
    if (ms == null || Number.isNaN(ms)) return '';
    const p = kstParts(ms);
    return `${pad(p.hh)}:${pad(p.mm)}`;
}

// ── 오류 판정
// 서버(callable) 미배포: 함수가 없으면 404(not-found) 또는 CORS 실패(internal)로 오고 details.code 가 없다.
// 그래서 dev-server 계약: **서버가 던지는 모든 오류에는 details.code 를 붙인다** — details 없는 not-found/internal/unimplemented = 서버 준비 중.
export function isReceptionServerNotReady(e) {
    const code = String(e?.code || '').replace(/^functions\//, '');
    if (e?.details?.code) return false;
    return code === 'not-found' || code === 'internal' || code === 'unimplemented';
}
export const RECEPTION_SERVER_NOT_READY = '접수 서버 준비 중입니다.';

// 환자 휴대폰 화면 오류 문구 — 할 일(데스크에 말씀)을 함께
// 치과 처리위탁 확인 전(서버 clinic-consent-required · resolveClinicWalkin deskReady:false) — 계약 원문 문구, 두 곳이 같은 문구를 쓴다
export const CLINIC_CONSENT_REQUIRED_MSG = '이 치과는 아직 QR 접수를 시작하지 않았습니다. 데스크에서 접수해 주세요';

export function receptionPatientError(e, fallback) {
    const code = String(e?.code || '').replace(/^functions\//, '');
    const detail = e?.details?.code;
    if (isReceptionServerNotReady(e)) return `${RECEPTION_SERVER_NOT_READY} 데스크에 말씀해 주세요.`;
    if (detail === 'feature-off') return '지금은 QR 접수를 받지 않습니다. 데스크에 말씀해 주세요.';
    if (detail === 'slug-not-found') return '확인할 수 없는 QR입니다. 데스크에 말씀해 주세요.';
    if (detail === 'ticket-not-found') return '접수 내역을 찾을 수 없습니다. 데스크에 말씀해 주세요.';
    // 10-04 추가 계약 — 치과 처리위탁 확인 게이트(문구는 계약 원문)
    if (detail === 'clinic-consent-required') return CLINIC_CONSENT_REQUIRED_MSG;
    if (detail === 'consent-required' || detail === 'consent-mismatch') return '동의 내용이 최신이 아닙니다. 화면을 새로고침한 뒤 다시 접수해 주세요.';
    // 만 14세 미만 — 보호자(법정대리인) 경로 (정본 §7-3)
    if (detail === 'guardian-required') return '생년월일을 보면 만 14세 미만입니다. 위에서 [부모님이 대신 적어요]를 눌러 부모님이 적어 주세요.';
    if (detail === 'guardian-not-ready') return '만 14세 미만은 지금은 보호자와 함께 데스크에서 접수해 주세요.';
    if (detail === 'age-mode-mismatch') return '생년월일을 보면 만 14세 이상입니다. 위에서 [본인이 적어요]를 눌러 주세요. 혼자 적기 어려우시면 데스크에 말씀해 주세요.';
    if (detail === 'invalid-age-mode') return '누가 적는지 다시 골라 주세요.';
    if (detail === 'invalid-guardian-name') return `보호자 이름을 확인해 주세요 (${WALKIN_NAME_HINT}).`;
    if (detail === 'invalid-guardian-relation') return '아이와의 관계를 골라 주세요.';
    if (detail === 'field-not-allowed') return '입력한 내용을 다시 확인해 주세요. 화면을 새로고침한 뒤 다시 접수해 주세요.';
    if (detail === 'rrn-not-allowed') return '주민등록번호는 적을 수 없습니다. 이름·주소를 확인해 주세요.';
    if (detail === 'invalid-name') return `이름을 확인해 주세요 (${WALKIN_NAME_HINT}).`;
    if (detail === 'invalid-birth') return '생년월일을 1970-01-01 형식으로 적어 주세요.';
    if (detail === 'invalid-sex') return '성별을 골라 주세요.';
    if (detail === 'invalid-tel') return '휴대폰 번호를 010-0000-0000 형식으로 적어 주세요.';
    if (detail === 'invalid-addr') return '주소를 확인해 주세요.';
    if (detail === 'invalid-reason') return '오늘 오신 이유를 골라 주세요.';
    if (code === 'resource-exhausted') return '지금은 접수가 많습니다. 데스크에 말씀해 주세요.';
    if (code === 'invalid-argument') return '입력한 내용을 다시 확인해 주세요.';
    if (code === 'unavailable' || code === 'deadline-exceeded') return '인터넷 연결이 불안정합니다. 잠시 후 다시 눌러 주세요.';
    return fallback || '접수하지 못했습니다. 데스크에 말씀해 주세요.';
}

// 접수 삭제 사유 (서버 deleteReceptionEntry reason key) — 대표만, 되돌릴 수 없음
export const RECEPTION_DELETE_REASONS = [
    { key: 'patient_request', label: '환자 요청' },
    { key: 'wrong_entry', label: '잘못 접수' },
    { key: 'spam', label: '장난 접수' },
    { key: 'other', label: '기타' },
];

// 접수 삭제 오류 문구 — 삭제 전용 코드 먼저, 나머지는 데스크 공통
export function receptionDeleteError(e) {
    const detail = e?.details?.code;
    if (detail === 'owner-only') return '치과 대표 계정만 접수를 삭제할 수 있습니다.';
    if (detail === 'reception-not-found') return '이미 삭제되었거나 찾을 수 없는 접수입니다. 화면을 새로고침해 주세요.';
    // 서버 계약(10-04): invalid-argument invalid-delete-reason / unavailable delete-failed(접속기록 포함 트랜잭션 실패 — 전부 또는 전무)
    if (detail === 'invalid-delete-reason') return '삭제 사유를 골라 주세요.';
    if (detail === 'delete-failed') return '지금은 삭제할 수 없습니다. 아무것도 지워지지 않았으니 잠시 후 다시 시도해 주세요.';
    return receptionDeskError(e, '삭제하지 못했습니다. 잠시 후 다시 눌러 주세요.');
}

// 데스크 직접 추가(addDeskWalkin) 오류 문구 — 추가 전용 코드 먼저, 나머지는 데스크 공통
export function receptionDeskAddError(e) {
    const code = String(e?.code || '').replace(/^functions\//, '');
    const detail = e?.details?.code;
    if (isReceptionServerNotReady(e)) return `${RECEPTION_SERVER_NOT_READY} 직접 추가는 서버 기능이 배포된 뒤 쓸 수 있습니다.`;
    if (detail === 'feature-off') return '접수 기능이 아직 열리지 않았습니다.';
    if (detail === 'desk-add-not-ready') return '데스크 직접 추가는 아직 준비 중입니다.';
    if (detail === 'desk-add-not-yet') {
        const from = e?.details?.from;
        return from ? `직접 추가는 ${fmtMonthDay(from)}부터 사용할 수 있습니다.` : '직접 추가는 아직 사용할 수 없습니다.';
    }
    if (detail === 'desk-add-consent-required') return '직접 추가를 쓰려면 치과 대표의 처리위탁 확인(새 판)이 한 번 더 필요합니다.';
    if (detail === 'field-not-allowed') return '입력한 내용을 다시 확인해 주세요. 화면을 새로고침한 뒤 다시 넣어 주세요.';
    if (detail === 'invalid-name') return `이름을 확인해 주세요 (${WALKIN_NAME_HINT}).`;
    if (detail === 'rrn-not-allowed') return '이름 칸에는 이름만 적어 주세요. 주민등록번호는 적을 수 없습니다.';
    if (detail === 'invalid-reason') return '오신 이유를 다시 골라 주세요.';
    if (detail === 'desk-add-limit') return '지금은 직접 추가가 너무 많아 더 넣을 수 없습니다. 잠시 후 다시 시도해 주세요.';
    // 접속기록 포함 트랜잭션 실패(전부 또는 전무) — 저장된 것 없음
    if (detail === 'desk-add-failed') return '지금은 추가할 수 없습니다. 아무것도 저장되지 않았으니 잠시 후 다시 눌러 주세요.';
    if (code === 'resource-exhausted') return '지금은 직접 추가가 너무 많아 더 넣을 수 없습니다. 잠시 후 다시 시도해 주세요.';
    return receptionDeskError(e, '추가하지 못했습니다. 잠시 후 다시 눌러 주세요.');
}

// 데스크 직접 추가 빼기(removeDeskWalkin) 오류 문구
export function receptionDeskRemoveError(e) {
    const detail = e?.details?.code;
    if (detail === 'reception-not-found') return '이미 빠졌거나 찾을 수 없습니다. 화면을 새로고침해 주세요.';
    if (detail === 'not-desk-entry') return 'QR로 받은 접수는 여기서 뺄 수 없습니다. 치과 대표가 환자 정보 칸의 [이 접수 삭제]로 지워 주세요.';
    if (detail === 'reception-closed') return '오늘 접수는 마감되었습니다. 화면을 새로고침해 주세요.';
    if (detail === 'desk-remove-failed') return '지금은 뺄 수 없습니다. 아무것도 바뀌지 않았으니 잠시 후 다시 눌러 주세요.';
    return receptionDeskError(e, '빼지 못했습니다. 잠시 후 다시 눌러 주세요.');
}

// 치과(데스크) 화면 오류 문구
export function receptionDeskError(e, fallback) {
    const code = String(e?.code || '').replace(/^functions\//, '');
    const detail = e?.details?.code;
    if (isReceptionServerNotReady(e)) return `${RECEPTION_SERVER_NOT_READY} 서버 기능이 배포되면 이 화면에 접수가 표시됩니다.`;
    if (detail === 'feature-off') return '접수 기능이 아직 열리지 않았습니다.';
    if (detail === 'reception-not-found') return '이 접수 기록을 찾을 수 없습니다 (보관 기간이 지났을 수 있습니다).';
    if (detail === 'already-called') return '이미 호출한 환자입니다.';
    if (detail === 'order-not-found') return '의뢰서를 찾을 수 없습니다.';
    // linkOrderToReception — 기공소 자동 수집 의뢰서(autoCollected·agent_auto)는 연결 불가(10-04 서버 계약, failed-precondition)
    if (detail === 'order-not-linkable') return '기공소가 자동으로 모은 의뢰서는 접수와 연결할 수 없습니다.';
    // linkOrderToReception — 데스크 직접 추가 건은 연결하지 않는다(그날만·기록 아님, 10-04 서버 계약)
    if (detail === 'reception-not-linkable') return '데스크에서 직접 추가한 환자는 의뢰서와 연결하지 않습니다(이름만 채웁니다).';
    // 마감(지난 날) 접수 호출 — 다시 눌러도 같은 결과라 재시도 유도 금지
    if (detail === 'reception-closed') return '오늘 접수는 마감되었습니다. 화면을 새로고침해 주세요.';
    // linkOrderToReception — 의뢰서 작성일과 접수일이 다름 / 접수당 연결 상한(서버 ORDER_IDS_MAX=20)
    if (detail === 'reception-day-mismatch') return '의뢰서 작성일과 같은 날 접수만 연결할 수 있습니다.';
    if (detail === 'reception-orders-full') return '한 접수에는 의뢰서를 20건까지 연결할 수 있습니다. 이 접수에는 더 연결할 수 없습니다.';
    // 접속기록 실패 시 열람 거부(fail-closed) — unavailable 이지만 인터넷 문제가 아님
    if (detail === 'access-log-failed') return '열람 기록을 남기지 못해 열지 않았습니다. 잠시 뒤 다시 열어 주세요.';
    // 치과 처리위탁 확인(acceptDeskClinicConsent)
    if (detail === 'owner-only') return '치과 대표 계정만 확인할 수 있습니다.';
    if (detail === 'consent-mismatch') return '확인 내용이 최신이 아닙니다. 화면을 새로고침해 주세요.';
    if (code === 'unauthenticated') return '로그인이 필요합니다. 다시 로그인해 주세요.';
    // 가입 승인 전 치과 — 서버 resolveClinicActor(clinic-patients.js)가 details 없이 permission-denied + 이 메시지로 던진다
    if (code === 'permission-denied' && String(e?.message || '').includes('가입 승인')) return '가입 승인 후 사용할 수 있습니다.';
    if (code === 'permission-denied') return '치과 계정(대표·직원)만 사용할 수 있습니다.';
    if (code === 'resource-exhausted') return '요청이 많아 잠시 제한되었습니다. 잠시 후 다시 시도해 주세요.';
    if (code === 'unavailable' || code === 'deadline-exceeded') return '인터넷 연결이 불안정합니다. 잠시 후 다시 시도해 주세요.';
    return fallback || '접수 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.';
}
